<template>
	<div class="validation-table-excel" @keydown.capture="onEditorKeydown">
		<SpeedDial
			v-if="speedDialActions.length > 0"
			:model="speedDialActions"
			direction="up"
			class="validation-table-excel__speed-dial"
			:transitionDelay="80"
			buttonClass="validation-table-excel__speed-dial-button"
			:tooltipOptions="{ position: 'left' }"
			@show="isSpeedDialOpen = true"
			@hide="isSpeedDialOpen = false"
			:pt="{
				action: {
					class: 'validation-table-excel__speed-dial-action',
				},
				actionIcon: {
					class: 'validation-table-excel__speed-dial-action-icon',
				},
			}"
		>
			<template #button="{ toggleCallback }">
				<Button
					v-tooltip.left="$t('Squeeze.Validation.Buttons.TableOptions')"
					type="button"
					:icon="isSpeedDialOpen ? 'mdi mdi-table-arrow-up' : 'mdi mdi-table-arrow-down'"
					class="p-speeddial-button p-button-rounded validation-table-excel__speed-dial-button"
					:aria-label="$t('Squeeze.Validation.Buttons.TableOptions')"
					:aria-expanded="isSpeedDialOpen"
					aria-haspopup="true"
					@click="toggleCallback"
				/>
			</template>
		</SpeedDial>

		<div class="validation-table-excel__editor" @mousedown.capture="onEditorMouseDown">
			<vue-excel-editor
				:key="editorInstanceKey"
				ref="excelEditor"
				v-model="editorRows"
				v-model:selectedCount="selectedCount"
				:readonly="isReadOnlyMode"
				:no-paging="true"
				:no-mouse-scroll="true"
				:filter-row="false"
				:no-num-col="false"
				:localized-label="localizedLabel"
				:row-style="getRowStyle"
				:cell-style="getCellStyle"
				:no-header-edit="true"
				:disable-panel-setting="true"
				disable-panel-filter
				no-mass-update
				enterToSouth
				width="100%"
				no-footer
				@setting="onEditorSetting"
				@select="onRowSelectionChange"
				@cell-focus="onCellFocus"
				@delete="onDeleteRows"
			>
				<vue-excel-column
					field="$id"
					type="string"
					label=""
					key-field
					invisible
					readonly
				/>
				<vue-excel-column
					field="rowPosition"
					type="number"
					:label="$t('Squeeze.Validation.General.PositionHeadline')"
					width="64px"
					:change="onRowPositionChange"
				/>
				<vue-excel-column
					v-for="(column, columnIndex) in visibleColumns"
					:key="column.id ?? column.name"
					:field="column.name ?? ''"
					:label="getTranslatedDescription(column) || column.name || ''"
					:type="getColumnType(column)"
					:width="getColumnWidth(column, columnIndex)"
					:autoFillWidth="columnIndex === visibleColumns.length - 1"
					:readonly="column.readonly ? true : undefined"
					:autocomplete="isLookupColumn(column)"
					:options="getLookupOptions(column)"
					:to-value="toEditorValue"
					:to-text="toEditorText"
					:change="(newValue: string, oldValue: string, row: ValidationTableRowView) => onCellChange(column, newValue, oldValue, row)"
				/>
			</vue-excel-editor>
		</div>
		<ValidationTableExcelReorderDialog
			v-model:visible="showReorderDialog"
			:title="$t('Squeeze.Validation.Buttons.ChangeSorting')"
			:rows="reorderDialogRows"
			:readonly="isReadOnlyMode"
			@reorder="onRowsReordered"
		/>
	</div>
</template>

<script lang="ts">
/* eslint max-lines: off */
import { ComponentPublicInstance, computed, defineComponent, nextTick, onMounted, PropType, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import SpeedDial from "primevue/speeddial";
import { useSqueezeStore } from "@/apps/squeeze/store";
import ValidationTableExcelReorderDialog, { ValidationTableExcelReorderItem } from "@/apps/squeeze/components/ValidationTableExcelReorderDialog.vue";
import { ClientManager } from "@/singletons/ClientManager";
import { ToastManager } from "@/util/ToastManager";
import { TableStatus } from "@/util/TableStatus";
import { formatCellValue, getEditorColumnType, isNumericColumn, normalizeCellValue } from "@/util/ValidationTableCellFormat";
import { useValidationColumnWidths } from "@/apps/squeeze/composables/useValidationColumnWidths";
import { getTranslatedDescription } from "@/util/TranslationHelper";
import {
	CustomValidationActionDto,
	DocumentField,
	DocumentTable,
	DocumentTableCell,
	DocumentTableColumn,
	DocumentTableRow,
	ValidationFieldDto
} from "@dex/squeeze-client-ts";

type DocumentTableRowWithId = DocumentTableRow & {
	$id: string;
};

type ValidationTableRowView = Record<string, string | number> & {
	$id: string;
	rowPosition: string;
};

type LookupDisplayOption = {
	label: string;
	value?: string;
};

type ExcelEditorRef = ComponentPublicInstance<{
	deleteSelectedRecords?: () => void;
	clearAllSelected?: () => void;
	calAutocompleteList?: (force?: boolean) => void;
	moveTo?: (rowPos: number, colPos?: number) => boolean;
	moveNorth?: () => boolean;
	currentField?: { autocomplete?: boolean; name?: string };
	inputBoxShow?: number | boolean;
	inputBox?: HTMLTextAreaElement;
	inputSquare?: HTMLElement;
	labelTr?: HTMLElement;
	tableContent?: HTMLElement;
}>;

/** Cell/row validation states as reported by the backend. */
const CELL_STATE = {
	OK: "OK",
	ERROR: "ERROR",
	FORCE_APPROVAL: "FORCEAPPROVAL",
} as const;

const ERROR_ROW_BACKGROUND = "rgba(255, 0, 0, 0.04)";
const ERROR_CELL_SHADOW = "inset 0 0 0 2px var(--error-color)";
const WARNING_CELL_SHADOW = "inset 0 0 0 2px var(--warning-color)";
const OK_CELL_SHADOW = "inset 0 0 0 0";
const SMALL_ROW_HEIGHT = "2.438rem";

export default defineComponent({
	name: "ValidationTableExcel",
	components: {
		ValidationTableExcelReorderDialog,
		SpeedDial,
	},
	props: {
		documentFields: {
			type: Array as PropType<ValidationFieldDto[]>,
			default: () => [],
		},
		currentTable: {
			type: Object as PropType<DocumentTable>,
			default: () => ({}),
		},
		hideButtons: {
			type: Boolean,
			default: false,
		},
		isReadOnlyMode: {
			type: Boolean,
			default: false,
		},
		documentId: {
			type: Number,
			required: true,
		},
		documentClassId: {
			type: Number,
			required: true,
		},
		resetSelectedRows: {
			type: Boolean,
			default: false,
		},
		customTableActions: {
			type: Array as PropType<CustomValidationActionDto[]>,
			default: () => [],
		},
	},
	emits: [
		"onFocusField",
		"onChange",
		"onChangeTable",
		"validationStartup",
		"allTableRefCells",
		"loadingStatusChange",
		"mountedReady",
		"focusLastHeadField",
		"executeCustomTableAction",
	],
	setup(props, { emit }) {
		const { t } = useI18n();
		const toast = useToast();
		const store = useSqueezeStore();
		const tableLoadingStatusText = {
			requesting: TableStatus.getValidationTableLoadingStatusText(t, "requesting"),
			preparing: TableStatus.getValidationTableLoadingStatusText(t, "preparing"),
			loadingWidths: TableStatus.getValidationTableLoadingStatusText(t, "loadingWidths"),
			ready: TableStatus.getValidationTableLoadingStatusText(t, "ready"),
		};
		const documentService = ClientManager.getInstance().squeeze.document;
		const excelEditor = ref<ExcelEditorRef | null>(null);
		const selectedCount = ref(0);
		const table = reactive<DocumentTable>({});
		const editorRows = ref<ValidationTableRowView[]>([]);
		const isMounted = ref(false);
		const isSpeedDialOpen = ref(false);
		const hasShownInitialCellValidationStatus = ref(false);
		const lastLoadingStatus = ref("");
		const showReorderDialog = ref(false);
		let nextRowId = 1;

		const visibleColumns = computed(() => {
			return (table.columns ?? []).filter((column) => !column.hidden && column.name);
		});

		const visibleColumnsByName = computed(() => {
			const nextVisibleColumnsByName = new Map<string, DocumentTableColumn>();
			visibleColumns.value.forEach((column) => {
				if (column.name) {
					nextVisibleColumnsByName.set(column.name, column);
				}
			});
			return nextVisibleColumnsByName;
		});

		const getVisibleColumn = (columnName?: string) => {
			if (!columnName) {
				return undefined;
			}

			return visibleColumnsByName.value.get(columnName);
		};

		const lookupValueByColumnAndText = new Map<string, string>();

		// Column-width persistence (server + localStorage) and resize tracking live in their own composable.
		const columnWidths = useValidationColumnWidths({
			table,
			getDocumentClassId: () => props.documentClassId,
			visibleColumns,
			getVisibleColumn,
			toast,
			t: (key: string) => t(key),
		});

		const editorInstanceKey = computed(() => {
			const columnKey = (table.columns ?? [])
				.map((column) => `${column.id ?? "columnIdMissing"}`)
				.join("|");
			return `${table.id ?? "tableIdMissing"}::${columnKey}::widths-${columnWidths.widthsVersion.value}`;
		});

		const localizedLabel = computed(() => ({
			footerLeft: (top: number, bottom: number, total: number) => `${top} - ${bottom} / ${total}`,
			first: t("Squeeze.Validation.TableFooter.First"),
			previous: t("Squeeze.Validation.TableFooter.Previous"),
			next: t("Squeeze.Validation.TableFooter.Next"),
			last: t("Squeeze.Validation.TableFooter.Last"),
			footerRight: {
				selected: t("Squeeze.Validation.TableFooter.Selected") + ': ',
				filtered: t("Squeeze.Validation.TableFooter.Filtered") + ': ',
				loaded: t("Squeeze.Validation.TableFooter.Loaded") + ': ',
			},
			processing: t("Squeeze.General.Loading"),
			tableSetting: t("Squeeze.Validation.Buttons.ChangeSorting"),
			exportExcel: "Excel",
			importExcel: "Excel",
			back: t("Squeeze.Validation.Buttons.Abort"),
			reset: t("Squeeze.Validation.TableFooter.Reset"),
			sortingAndFiltering: t("Squeeze.General.Options"),
			sortAscending: t("Squeeze.Validation.TableFooter.SortAscending"),
			sortDescending: t("Squeeze.Validation.TableFooter.SortDescending"),
			apply: t("Squeeze.Validation.TableFooter.Apply"),
			noRecordIndicator: t("Squeeze.General.NoEntriesFound", { entryName: t("Squeeze.Validation.General.Position") }),
		}));

		const setLoadingStatus = (status: string) => {
			if (lastLoadingStatus.value === status) {
				return;
			}

			lastLoadingStatus.value = status;
			emit("loadingStatusChange", status);
		};

		const allowUiToRender = async () => {
			await nextTick();
			await new Promise<void>((resolve) => {
				window.setTimeout(() => resolve(), 0);
			});
		};

		const showInitialCellValidationStatus = async () => {
			if (hasShownInitialCellValidationStatus.value) {
				return;
			}

			hasShownInitialCellValidationStatus.value = true;
			emit("validationStartup");
			await allowUiToRender();
		};

		const assignLocalTable = (sourceTable?: DocumentTable | null) => {
			const previousColumnsByName = new Map(
				(table.columns ?? [])
					.filter((column) => column.name)
					.map((column) => [column.name as string, column])
			);
			const nextColumns = sourceTable?.columns?.length
				? sourceTable.columns.map((column) => ({
					...(column.name ? previousColumnsByName.get(column.name) ?? {} : {}),
					...column,
				}))
				: (table.columns ?? []).map((column) => ({ ...column }));

			Object.assign(table, {
				...(sourceTable ?? {}),
				rows: sourceTable?.rows ?? [],
				columns: nextColumns,
			});
		};

		const toEditorValue = (value: string, _row?: unknown, field?: { name?: string }) => {
			const column = getVisibleColumn(field?.name);
			const label = String(value ?? "");
			const lookupValue = field?.name ? lookupValueByColumnAndText.get(`${field.name}::${label}`) : undefined;
			return column ? normalizeCellValue(column, lookupValue ?? label) : label;
		};

		const toEditorText = (value: string, _row?: unknown, field?: { name?: string }) => {
			const normalizedValue = String(value ?? "");
			const column = getVisibleColumn(field?.name);
			return column ? formatCellValue(column, normalizedValue) : normalizedValue;
		};

		const createRowId = () => {
			return `row-${nextRowId++}`;
		};

		const getTableRowId = (row: DocumentTableRow) => {
			const targetRow = row as DocumentTableRowWithId;
			if (!targetRow.$id) {
				targetRow.$id = createRowId();
			}

			return targetRow.$id;
		};

		const buildCellsByName = (row: DocumentTableRow) => {
			const cellsByName: Record<string, DocumentTableCell> = {};
			row.cells?.forEach((cell) => {
				if (cell.columnName) {
					cellsByName[cell.columnName] = cell;
				}
			});
			return cellsByName;
		};

		const rowIndexesById = computed(() => {
			const nextRowIndexesById = new Map<string, number>();
			(table.rows ?? []).forEach((row, index) => {
				nextRowIndexesById.set(getTableRowId(row), index);
			});
			return nextRowIndexesById;
		});

		const tableRowsById = computed(() => {
			const nextTableRowsById = new Map<string, DocumentTableRow>();
			(table.rows ?? []).forEach((row) => {
				nextTableRowsById.set(getTableRowId(row), row);
			});
			return nextTableRowsById;
		});

		const getRowIndexById = (rowId: string) => {
			return rowIndexesById.value.get(rowId) ?? -1;
		};

		const getRowCell = (rowId: string, columnName: string) => {
			return tableRowsById.value.get(rowId)?.cells?.find((cell) => cell.columnName === columnName);
		};

		const createEditorRow = (row: DocumentTableRow, index: number): ValidationTableRowView => {
			const cellsByName = buildCellsByName(row);
			const editorRow: ValidationTableRowView = {
				$id: getTableRowId(row),
				rowPosition: String(index + 1),
			};

			visibleColumns.value.forEach((column) => {
				if (column.name) {
					editorRow[column.name] = cellsByName[column.name]?.value ?? "";
				}
			});

			return editorRow;
		};

		const rebuildEditorRows = () => {
			editorRows.value = (table.rows ?? []).map((row, index) => createEditorRow(row, index));
		};

		const emitTableChange = (changedRowPos?: number | null) => {
			emit("onChangeTable", table, true, changedRowPos);
			emit("onChange");
		};

		const emitTableReorder = () => {
			emit("onChangeTable", table);
		};

		const moveRowToPosition = (currentRowIndex: number, requestedPosition: string) => {
			if (!table.rows?.length || currentRowIndex < 0) {
				return false;
			}

			const parsedPosition = Number.parseInt(requestedPosition, 10);
			if (Number.isNaN(parsedPosition)) {
				return false;
			}

			const targetRowIndex = Math.min(Math.max(parsedPosition - 1, 0), table.rows.length - 1);
			if (targetRowIndex === currentRowIndex) {
				return false;
			}

			const movedRows = [...table.rows];
			const [movedRow] = movedRows.splice(currentRowIndex, 1);
			if (!movedRow) {
				return false;
			}

			movedRows.splice(targetRowIndex, 0, movedRow);
			table.rows = movedRows;
			rebuildEditorRows();
			emitTableReorder();
			return true;
		};

		function getRowPreview(row: ValidationTableRowView) {
			const previewValues = visibleColumns.value
				.slice(0, 2)
				.map((column) => (column.name ? String(row[column.name] ?? "") : ""))
				.filter((value) => value.length > 0);

			return previewValues.join(" | ");
		}

		const reorderDialogRows = computed<ValidationTableExcelReorderItem[]>(() => {
			return editorRows.value.map((row) => ({
				$id: row.$id,
				rowPosition: row.rowPosition,
				preview: getRowPreview(row),
			}));
		});

		const createEmptyRowModelObject = () => {
			return {
				cells: (table.columns ?? []).map((column) => ({
					columnName: column.name,
					value: "",
				})) as DocumentTableCell[],
				value: {
					value: "",
					page: 0,
					text: "",
					type: "",
					confidence: 0,
				},
				$id: createRowId(),
			} as DocumentTableRowWithId;
		};

		const cloneRow = (row: DocumentTableRow): DocumentTableRow => {
			return {
				...row,
				cells: row.cells?.map((cell) => ({ ...cell })) ?? [],
				value: row.value ? JSON.parse(JSON.stringify(row.value)) : row.value,
				$id: createRowId(),
			} as DocumentTableRowWithId;
		};

		const syncCellValue = (rowIndex: number, columnName: string, value: string) => {
			const targetRow = table.rows?.[rowIndex];
			const targetCell = targetRow?.cells?.find((cell) => cell.columnName === columnName);
			const targetEditorRow = editorRows.value[rowIndex];
			const column = getVisibleColumn(columnName);
			if (!targetCell) {
				return;
			}

			const normalizedValue = column ? normalizeCellValue(column, value) : value;
			targetCell.value = normalizedValue;
			if (targetCell.state === CELL_STATE.FORCE_APPROVAL) {
				targetCell.state = CELL_STATE.OK;
			}

			if (targetEditorRow) {
				targetEditorRow[columnName] = normalizedValue;
			}
		};

		/** Row a pending insert wants focused after the next re-render (the validation rebuild). */
		let pendingFocusRowIndex: number | null = null;

		const ensureFocusedEditorCellVisible = () => {
			requestAnimationFrame(() => {
				const editor = excelEditor.value;
				const tableContent = editor?.tableContent;
				const inputSquare = editor?.inputSquare;
				if (!tableContent || !inputSquare) {
					return;
				}

				const tableContentRect = tableContent.getBoundingClientRect();
				const inputSquareRect = inputSquare.getBoundingClientRect();
				const headerBottom = editor?.labelTr instanceof HTMLElement
					? editor.labelTr.getBoundingClientRect().bottom
					: tableContentRect.top;
				const topBoundary = Math.max(tableContentRect.top, headerBottom) + 4;
				const bottomBoundary = tableContentRect.bottom - 4;

				if (inputSquareRect.bottom > bottomBoundary) {
					tableContent.scrollTop += inputSquareRect.bottom - bottomBoundary;
				} else if (inputSquareRect.top < topBoundary) {
					tableContent.scrollTop -= topBoundary - inputSquareRect.top;
				}
			});
		};

		/** Focuses a row's first data cell and scrolls it into view */
		const focusEditorRow = async (rowIndex: number) => {
			if (rowIndex < 0 || rowIndex >= editorRows.value.length) {
				return;
			}

			await allowUiToRender();
			excelEditor.value?.moveTo?.(rowIndex, 2);
			await allowUiToRender();
			ensureFocusedEditorCellVisible();
		};

		const rebuildFromCurrentTable = async (sourceTable?: DocumentTable | null) => {
			setLoadingStatus(tableLoadingStatusText.preparing);
			columnWidths.hasUserResizedColumn.value = false;
			assignLocalTable(sourceTable ?? props.currentTable);
			setLoadingStatus(tableLoadingStatusText.loadingWidths);
			await columnWidths.loadColumnWidths();
			rebuildEditorRows();
			await nextTick();
			emit("allTableRefCells", []);
			setLoadingStatus(tableLoadingStatusText.ready);

			// Apply the focus after insert requested
			if (pendingFocusRowIndex !== null) {
				const rowIndex = pendingFocusRowIndex;
				pendingFocusRowIndex = null;
				await focusEditorRow(rowIndex);
			}
		};

		const onCellChange = async (column: DocumentTableColumn, newValue: string, oldValue: string, row: ValidationTableRowView) => {
			if (!column.name || newValue === oldValue) {
				return true;
			}

			await showInitialCellValidationStatus();
			const rowIndex = getRowIndexById(row.$id);
			if (rowIndex < 0) {
				return false;
			}

			syncCellValue(rowIndex, column.name, newValue);
			await nextTick();
			await allowUiToRender();
			emitTableChange(rowIndex + 1);
			return true;
		};

		const onRowPositionChange = async (newValue: string, oldValue: string, row: ValidationTableRowView) => {
			if (newValue === oldValue) {
				return true;
			}

			return moveRowToPosition(getRowIndexById(row.$id), newValue);
		};

		const onRowsReordered = (rowIds: string[]) => {
			if (!table.rows?.length) {
				return;
			}

			const rowIdSet = new Set(rowIds);
			const reorderedRows = rowIds
				.map((rowId) => tableRowsById.value.get(rowId))
				.filter((row): row is DocumentTableRow => Boolean(row));
			const remainingRows = table.rows.filter((row) => !rowIdSet.has(getTableRowId(row)));
			table.rows = [...reorderedRows, ...remainingRows];
			rebuildEditorRows();
			emitTableReorder();
		};

		/**
		 * Deletes a row, identified either by its editor row object or by a 1-based row position
		 * (the position form is used by the parent's keyboard shortcut).
		 * @param rowOrPosition
		 */
		const deleteRow = (rowOrPosition: ValidationTableRowView | number) => {
			if (!table.rows?.length) {
				return;
			}

			const rowIndex = typeof rowOrPosition === "number"
				? rowOrPosition - 1
				: getRowIndexById(rowOrPosition.$id);
			if (rowIndex === -1) {
				return;
			}

			table.rows.splice(rowIndex, 1);
			rebuildEditorRows();
			const nextRowPos = table.rows.length ? Math.min(rowIndex + 1, table.rows.length) : undefined;
			emitTableChange(nextRowPos);
		};

		const getColumnNameFromEditorColPos = (colPos?: number) => {
			if (typeof colPos !== "number") {
				return undefined;
			}

			const visibleColumnIndex = colPos - 2;
			if (visibleColumnIndex < 0) {
				return undefined;
			}

			return visibleColumns.value[visibleColumnIndex]?.name;
		};

		const emitFocusField = (rowIndex: number, columnName?: string, rowOverride?: DocumentTableRow, colPos?: number) => {
			const row = rowOverride ?? table.rows?.[rowIndex];
			const resolvedColumnName = columnName ?? getColumnNameFromEditorColPos(colPos);
			const column = resolvedColumnName
				? visibleColumns.value.find((entry) => entry.name === resolvedColumnName)
				: visibleColumns.value.find((entry) => row?.cells?.some((cell) => cell.columnName === entry.name))
					?? visibleColumns.value[0];
			const cell = row?.cells?.find((entry) => entry.columnName === column?.name) ?? row?.cells?.[0];

			if (!row || !column || !cell) {
				return;
			}

			const field = JSON.parse(JSON.stringify(cell)) as DocumentField;
			field.description = `${getTranslatedDescription(column)} (${t("Squeeze.Validation.General.Row")} ${rowIndex + 1})`;
			field.value = {
				value: cell.value,
				state: cell.state,
				errorText: cell.errorText,
			};

			if (cell.errorCode !== -1 && cell.errorCode != null) {
				field.value.errorText = t(`Squeeze.Validation.ErrorCode.${cell.errorCode}`);
			} else if (cell.state?.toLowerCase() === "ok") {
				field.value.errorText = t("Squeeze.Validation.ErrorCode.0");
			}

			emit("onFocusField", cell, row, rowIndex + 1, field);
		};

		const onCellFocus = (payload: { rowPos: number; colPos?: number; cell: { name?: string }; record?: ValidationTableRowView }) => {
			ensureFocusedEditorCellVisible();

			if (payload.record) {
				const rowIndex = getRowIndexById(payload.record.$id);
				if (rowIndex >= 0) {
					emitFocusField(rowIndex, payload.cell?.name, tableRowsById.value.get(payload.record.$id), payload.colPos);
					return;
				}
			}

			if (payload.rowPos >= 0) {
				emitFocusField(payload.rowPos, payload.cell?.name, undefined, payload.colPos);
			}
		};

		const onRowSelectionChange = (rowPositions: number[], isSelected: boolean) => {
			if (!isSelected || !rowPositions.length) {
				return;
			}

			emitFocusField(rowPositions[0]);
		};

		const onDeleteRows = (records: Array<{ $id: string }>) => {
			if (!table.rows?.length) {
				return;
			}

			const rowIndexes = records
				.map((record) => getRowIndexById(record.$id))
				.filter((index) => index >= 0)
				.sort((left, right) => right - left);

			rowIndexes.forEach((index) => {
				table.rows?.splice(index, 1);
			});

			rebuildEditorRows();
			emitTableChange();
		};

		const addRow = async () => {
			if (!table.rows) {
				table.rows = [];
			}

			table.rows.push(createEmptyRowModelObject());
			rebuildEditorRows();
			pendingFocusRowIndex = table.rows.length - 1;
			emitTableChange(table.rows.length);
			await focusEditorRow(table.rows.length - 1);
		};

		/** Inserts a new empty row and focuses it. */
		const createNewRow = async (oneBasedPosition?: number | null) => {
			if (!table.rows) {
				table.rows = [];
			}

			const model = createEmptyRowModelObject();
			if (typeof oneBasedPosition === "number" && oneBasedPosition > 0) {
				table.rows.splice(oneBasedPosition, 0, model);
				rebuildEditorRows();
				pendingFocusRowIndex = oneBasedPosition;
				emitTableChange(oneBasedPosition + 1);
				await focusEditorRow(oneBasedPosition);
				return;
			}

			table.rows.push(model);
			rebuildEditorRows();
			pendingFocusRowIndex = table.rows.length - 1;
			emitTableChange(table.rows.length);
			await focusEditorRow(table.rows.length - 1);
		};

		/**
		 * Inserts a copy of the row at the given position directly below it, then focuses it.
		 * @param oneBasedPosition
		 */
		const copyRow = async (oneBasedPosition: number) => {
			if (!table.rows?.length || oneBasedPosition < 1) {
				return;
			}

			const sourceRow = table.rows[oneBasedPosition - 1];
			if (!sourceRow) {
				return;
			}

			table.rows.splice(oneBasedPosition, 0, cloneRow(sourceRow));
			rebuildEditorRows();
			pendingFocusRowIndex = oneBasedPosition;
			emitTableChange(oneBasedPosition + 1);
			await focusEditorRow(oneBasedPosition);
		};

		const deleteSelectedRows = () => {
			excelEditor.value?.deleteSelectedRecords?.();
		};

		const isLookupColumn = (column: DocumentTableColumn) => {
			return Boolean(column.id && column.lookup?.active);
		};

		const getLookupResultColumnValue = (row: { displayColumnResults?: Record<string, string> }, columnId?: number) => {
			if (columnId == null) {
				return undefined;
			}

			return row.displayColumnResults?.[columnId] ?? row.displayColumnResults?.[String(columnId)];
		};

		const buildLookupLabel = (row: { displayColumnResults?: Record<string, string> }, columnIds: number[]) => {
			return columnIds
				.map((columnId) => getLookupResultColumnValue(row, columnId))
				.filter((entry): entry is string => typeof entry === "string" && entry.length > 0)
				.join(" | ");
		};

		const buildLookupOption = (
			row: { displayColumnResults?: Record<string, string> },
			resultKeyColumnId: number,
			resultValueColumnIds: number[]
		): LookupDisplayOption | undefined => {
			const label = buildLookupLabel(row, resultValueColumnIds);
			if (!label) {
				return undefined;
			}

			const value = getLookupResultColumnValue(row, resultKeyColumnId);
			return {
				label,
				...(typeof value === "string" && value.length > 0 ? { value } : {}),
			};
		};

		/**
		 * Fetches lookup display values for a table cell and returns them as a flat list of strings.
		 * Shared by the autocomplete dropdown ({@link getLookupOptions}) and the on-blur value
		 * check ({@link checkAutocompleteValues}).
		 * @param column
		 * @param fieldSearchValue
		 * @param documentTableRow
		 */
		const fetchLookupDisplayOptions = async (column: DocumentTableColumn, fieldSearchValue: string, documentTableRow: DocumentTableRow): Promise<LookupDisplayOption[]> => {
			const lookup = column.lookup;
			if (!lookup?.active || !column.id || !table.id) {
				return [];
			}

			const rows = await documentService.getDocumentTableCellLookupValues(props.documentId, table.id, column.id, {
				documentFields: props.documentFields,
				fieldSearchValue,
				documentTableRow,
			});
			const resultKeyColumnId = lookup.resultKeyColumnId;
			const resultColumns = lookup.resultValueColumnIds?.length
				? lookup.resultValueColumnIds
				: [resultKeyColumnId];
			return rows
				.map((row) => buildLookupOption(row, resultKeyColumnId, resultColumns))
				.filter((entry): entry is LookupDisplayOption => Boolean(entry));
		};

		const fetchLookupDisplayValues = async (column: DocumentTableColumn, fieldSearchValue: string, documentTableRow: DocumentTableRow): Promise<string[]> => {
			const options = await fetchLookupDisplayOptions(column, fieldSearchValue, documentTableRow);
			return options
				.map((option) => option.value)
				.filter((value): value is string => typeof value === "string" && value.length > 0);
		};

		type LookupOptionsFunction = (value: string, record?: ValidationTableRowView) => Promise<string[]>;
		const lookupOptionsByColumnId = new Map<number, LookupOptionsFunction>();

		/**
		 * Returns a async options-function for vue-excel-editor that loads
		 * the lookup values of a table cell, so the editor can show them as dropdown-list
		 * Only needed for Dropdown lists (CRTL + arrow key)
		 * @param column
		 */
		const getLookupOptions = (column: DocumentTableColumn) => {
			if (!isLookupColumn(column) || !column.id) {
				return undefined;
			}

			const columnId = column.id;
			let lookupOptions = lookupOptionsByColumnId.get(columnId);
			if (!lookupOptions) {
				lookupOptions = async (value: string, record?: ValidationTableRowView) => {
					const currentColumn = (table.columns ?? []).find((tableColumn) => tableColumn.id === columnId);
					const targetRow = record ? tableRowsById.value.get(record.$id) : undefined;
					if (!currentColumn || !targetRow) {
						return [];
					}

					try {
						const query = String(value ?? "");
						const minInputLength = currentColumn.lookup?.minInputLength ?? 1;
						if (query.length === 0 && minInputLength > 0) {
							return [];
						}

						const options = await fetchLookupDisplayOptions(currentColumn, query, targetRow);
						options.forEach((option) => {
							if (option.value) {
								lookupValueByColumnAndText.set(`${currentColumn.name}::${option.label}`, option.value);
							}
						});
						return options.map((option) => option.label);
					} catch (response) {
						return [];
					}
				};
				lookupOptionsByColumnId.set(columnId, lookupOptions);
			}

			return lookupOptions;
		};

		/** Editor keyboard shortcuts
		 * Shift+Enter → previous row
		 * Ctrl/Cmd+ArrowDown → open the cells dropdown
	    */
		const onEditorKeydown = (event: KeyboardEvent) => {
			const editor = excelEditor.value;

			// Enter already moves to the next row (enterToSouth); Shift+Enter moves to the previous one.
			if (event.key === "Enter" && event.shiftKey) {
				event.preventDefault();
				event.stopPropagation();
				editor?.moveNorth?.();
				return;
			}

			if (event.code !== "ArrowDown" || !(navigator.platform.match("MacIntel") ? event.metaKey : event.ctrlKey)) {
				return;
			}
			if (!editor?.calAutocompleteList) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			if (editor.currentField?.autocomplete && editor.currentField?.name) {
				const column = getVisibleColumn(editor.currentField.name);
				if (column?.lookup?.minInputLength !== 0) {
					return;
				}
			}

			if (!editor.inputBoxShow && editor.inputBox) {
				// Force-opened list should show all values, not be filtered by a stale input value
				editor.inputBox.value = "";
			}

			editor.calAutocompleteList(true);
		};

		const checkAutocompleteValues = async (
			event: { query?: string },
			column: DocumentTableColumn,
			_cell: DocumentTableCell,
			fieldValue: string = "",
			tableIndex: number
		) => {
			if (!fieldValue || !column.id || !column.name || !column.lookup?.active || !table.id) {
				return;
			}

			const targetRow = table.rows?.[tableIndex - 1];
			if (!targetRow) {
				return;
			}

			try {
				const displayValues = await fetchLookupDisplayValues(column, event.query ?? fieldValue, targetRow);
				const nextValue = displayValues.includes(fieldValue) ? fieldValue : "";
				syncCellValue(tableIndex - 1, column.name, nextValue);
			} catch (response: any) {
				ToastManager.showError(toast, t("Squeeze.General.Error"), response);
			}
		};

		const getRowStyle = (row: ValidationTableRowView) => {
			const targetRow = tableRowsById.value.get(row.$id);
			if (targetRow?.state === CELL_STATE.ERROR) {
				return {
					backgroundColor: ERROR_ROW_BACKGROUND,
				};
			}

			return {};
		};

		const getCellStyle = (row: ValidationTableRowView, field: { name: string }) => {
			const cell = getRowCell(row.$id, field.name);
			const style: Record<string, string> = {};

			if (store.state.userSettings.viewSettings.smallDistanceBetweenFields) {
				style.height = SMALL_ROW_HEIGHT;
			}

			if (cell?.state === CELL_STATE.ERROR) {
				style.boxShadow = ERROR_CELL_SHADOW;
			} else if (cell?.state === CELL_STATE.FORCE_APPROVAL) {
				style.boxShadow = WARNING_CELL_SHADOW;
			} else if (cell?.state === CELL_STATE.OK) {
				style.boxShadow = OK_CELL_SHADOW;
			}

			if (field.name && isNumericColumn(getVisibleColumn(field.name))) {
				style.textAlign = "right";
			}

			return style;
		};

		const resetColumnWidths = async () => {
			await columnWidths.resetColumnWidths();
		};

		const executeCustomTableAction = (actionId: string) => {
			emit("executeCustomTableAction", actionId);
		};

		const speedDialActions = computed(() => {
			if (props.hideButtons) {
				return [];
			}

			const customActions = props.customTableActions
				.filter((action) => action.id)
				.map((action) => ({
					label: action.description || action.name ||  "CustomAction " + action.id,
					icon: action.icon || "mdi mdi-help",
					command: () => {
						if (action.id) {
							executeCustomTableAction(action.id);
						}
					},
				}));
			return [
				{
					label: t("Squeeze.Validation.Buttons.DeleteSelectedRows"),
					icon: "mdi mdi-delete-sweep-outline",
					command: () => {
						if (selectedCount.value < 1 || props.isReadOnlyMode) {
							return;
						}
						deleteSelectedRows();
					},
					disabled: selectedCount.value < 1 || props.isReadOnlyMode,
				},
				{
					label: t("Squeeze.Validation.Buttons.ResetColumnWidths"),
					icon: "mdi mdi-table-column-width",
					command: () => {
						if (props.isReadOnlyMode) {
							return;
						}
						resetColumnWidths();
					},
					disabled: props.isReadOnlyMode,
				},
				{
					label: t("Squeeze.Validation.Buttons.AddRow"),
					icon: "mdi mdi-plus",
					command: () => {
						if (props.isReadOnlyMode) {
							return;
						}
						addRow();
					},
					disabled: props.isReadOnlyMode,
				},
				{
					label: t("Squeeze.Validation.Buttons.ChangeSorting"),
					icon: "mdi mdi-drag",
					command: () => {
						if (props.isReadOnlyMode) {
							return;
						}
						showReorderDialog.value = true;
					},
					disabled: props.isReadOnlyMode,
				},
				...customActions,
			];
		});

		onMounted(async () => {
			await rebuildFromCurrentTable();
			isMounted.value = true;
			emit("mountedReady");
		});

		watch(() => props.currentTable, async (nextTable) => {
			if (!isMounted.value) {
				return;
			}

			if (nextTable === table) {
				return;
			}

			await rebuildFromCurrentTable(nextTable);
		});

		watch(() => props.resetSelectedRows, () => {
			if (props.resetSelectedRows) {
				excelEditor.value?.clearAllSelected?.();
			}
		});

		return {
			getTranslatedDescription,
			excelEditor,
			editorRows,
			reorderDialogRows,
			selectedCount,
			speedDialActions,
			showReorderDialog,
			visibleColumns,
			editorInstanceKey,
			localizedLabel,
			addRow,
			checkAutocompleteValues,
			copyRow,
			createNewRow,
			deleteRow,
			deleteSelectedRows,
			focusFirstCell: () => focusEditorRow(0),
			getRowStyle,
			getCellStyle,
			getColumnType: getEditorColumnType,
			getColumnWidth: columnWidths.getColumnWidth,
			getLookupOptions,
			isSpeedDialOpen,
			isLookupColumn,
			onEditorKeydown,
			onEditorMouseDown: columnWidths.onEditorMouseDown,
			onRowPositionChange,
			onCellChange,
			onCellFocus,
			onDeleteRows,
			onEditorSetting: columnWidths.onEditorSetting,
			onRowSelectionChange,
			onRowsReordered,
			resetColumnWidths,
			toEditorText,
			toEditorValue,
			executeCustomTableAction,
		};
	},
});
</script>

<style scoped>
.validation-table-excel {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	min-height: 0;
	max-width: 100%;
	overflow: hidden;
}

/**
 * fixed speed dial to not be hidden by viewer
 */
.validation-table-excel :deep(.validation-table-excel__speed-dial) {
	position: fixed;
	right: 2rem;
	bottom: 2rem;
	z-index: 1000;
}

.validation-table-excel :deep(.validation-table-excel__speed-dial-button.p-button.p-button-icon-only.p-button-rounded) {
	width: 3rem;
	height: 3rem;
	padding: 0.75rem;
	margin-right: 0;
}

.validation-table-excel :deep(.validation-table-excel__speed-dial-button .p-button-icon),
.validation-table-excel :deep(.validation-table-excel__speed-dial-action-icon) {
	font-size: 1.25rem;
	line-height: 1;
}

.validation-table-excel :deep(.validation-table-excel__speed-dial-action) {
	width: 2.75rem;
	height: 2.75rem;
}

.validation-table-excel__editor {
	flex: 1;
	min-height: 0;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
}

.validation-table-excel__editor :deep(.vue-excel-editor) {
	display: block !important;
	width: 100% !important;
	height: 100%;
	max-width: 100%;
	overflow: hidden;
}

.validation-table-excel__editor :deep(.component-content) {
	width: 100%;
	height: 100%;
	min-width: 0;
	max-width: none;
	overflow: hidden;
}

.validation-table-excel__editor :deep(.table-content) {
	width: 100%;
	height: 100%;
	overflow: auto;
	overscroll-behavior: contain;
}

.validation-table-excel__editor :deep(.systable) {
	min-width: 100%;
	width: max-content;
}

</style>
